import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// ─── Rules ───────────────────────────────────────────────
export const fetchMessengerRules = createAsyncThunk(
  'messengerAutoreply/fetchRules',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/messenger-autoreply', { params });
      return response.data.rules || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch rules');
    }
  }
);

export const fetchMessengerRule = createAsyncThunk(
  'messengerAutoreply/fetchRule',
  async ({ ruleId }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/marketing/messenger-autoreply/${ruleId}`);
      return response.data.rule;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch rule');
    }
  }
);

export const createMessengerRule = createAsyncThunk(
  'messengerAutoreply/createRule',
  async (ruleData, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/messenger-autoreply', ruleData);
      return response.data.rule;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create rule');
    }
  }
);

export const updateMessengerRule = createAsyncThunk(
  'messengerAutoreply/updateRule',
  async ({ ruleId, updates }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/marketing/messenger-autoreply/${ruleId}`, updates);
      return response.data.rule;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update rule');
    }
  }
);

export const deleteMessengerRule = createAsyncThunk(
  'messengerAutoreply/deleteRule',
  async ({ ruleId }, { rejectWithValue }) => {
    try {
      await api.delete(`/marketing/messenger-autoreply/${ruleId}`);
      return ruleId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete rule');
    }
  }
);

export const testMessengerRule = createAsyncThunk(
  'messengerAutoreply/testRule',
  async ({ ruleId, testSenderId }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/marketing/messenger-autoreply/${ruleId}/test`,
        { testSenderId }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to test rule');
    }
  }
);

const initialState = {
  rules: [],
  currentRule: null,
  loading: false,
  error: null,
};

const messengerAutoreplySlice = createSlice({
  name: 'messengerAutoreply',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    selectRule: (state, action) => {
      state.currentRule = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch Rules
    builder
      .addCase(fetchMessengerRules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessengerRules.fulfilled, (state, action) => {
        state.loading = false;
        state.rules = action.payload;
      })
      .addCase(fetchMessengerRules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch Single Rule
    builder
      .addCase(fetchMessengerRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessengerRule.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRule = action.payload;
      })
      .addCase(fetchMessengerRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create Rule
    builder
      .addCase(createMessengerRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMessengerRule.fulfilled, (state, action) => {
        state.loading = false;
        state.rules.push(action.payload);
      })
      .addCase(createMessengerRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update Rule
    builder
      .addCase(updateMessengerRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMessengerRule.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.rules.findIndex((r) => r._id === action.payload._id);
        if (index !== -1) {
          state.rules[index] = action.payload;
        }
        if (state.currentRule?._id === action.payload._id) {
          state.currentRule = action.payload;
        }
      })
      .addCase(updateMessengerRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete Rule
    builder
      .addCase(deleteMessengerRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMessengerRule.fulfilled, (state, action) => {
        state.loading = false;
        state.rules = state.rules.filter((r) => r._id !== action.payload);
        if (state.currentRule?._id === action.payload) {
          state.currentRule = null;
        }
      })
      .addCase(deleteMessengerRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Test Rule
    builder
      .addCase(testMessengerRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(testMessengerRule.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(testMessengerRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, selectRule } = messengerAutoreplySlice.actions;
export default messengerAutoreplySlice.reducer;
